import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Package, LogOut, PawPrint, Clock, X, CalendarClock, Users } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

const STATUS_STYLES = {
  confirmed: "bg-green-100 text-green-700",
  pending: "bg-amber-100 text-amber-700",
  cancelled: "bg-red-100 text-red-700",
  completed: "bg-blue-100 text-blue-700",
};

const canManage = (status) => status === "pending" || status === "confirmed";

export default function Account() {
  const { customer, logout } = useCustomerAuth();
  const [bookings, setBookings] = useState([]);
  const [bundles, setBundles] = useState({ packs: [], remaining: 0 });
  const [waitlist, setWaitlist] = useState([]);
  const [reschedule, setReschedule] = useState(null); // booking being rescheduled
  const [rform, setRform] = useState({ date: "", time: "" });

  const load = useCallback(() => {
    api.get("/customer/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/customer/bundles").then((r) => setBundles(r.data)).catch(() => {});
    api.get("/customer/waitlist").then((r) => setWaitlist(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancelBooking = async (id) => {
    try {
      await api.post(`/customer/bookings/${id}/cancel`);
      toast.success("Session cancelled");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openReschedule = (b) => { setReschedule(b); setRform({ date: b.date || "", time: b.time || "" }); };

  const saveReschedule = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/customer/bookings/${reschedule.id}`, rform);
      toast.success("Session rescheduled");
      setReschedule(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="bg-white text-brand-ink min-h-screen">
      <PublicNav />
      <section className="pt-32 pb-24 px-6 max-w-4xl mx-auto" data-testid="account-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            {customer?.picture && <img src={customer.picture} alt="" className="w-14 h-14 rounded-full object-cover" data-testid="account-avatar" />}
            <div>
              <h1 className="font-display text-4xl" data-testid="account-name">{customer?.name || "My Account"}</h1>
              <p className="text-brand-ink/60 mt-1" data-testid="account-email">{customer?.email}</p>
            </div>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-brand-line font-medium hover:border-brand-red transition-colors" data-testid="account-logout-btn"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>

        <div className="mt-10 bg-brand-mist rounded-2xl p-6 flex items-center gap-4" data-testid="account-pack-balance">
          <div className="w-12 h-12 rounded-full bg-brand-red/10 flex items-center justify-center"><Package className="w-6 h-6 text-brand-red" /></div>
          <div>
            <div className="text-3xl font-display" data-testid="pack-remaining">{bundles.remaining}</div>
            <div className="text-sm text-brand-ink/60">session pack credits remaining</div>
          </div>
          <Link to="/book" className="ml-auto px-5 py-2.5 rounded-full bg-brand-red text-white text-sm font-medium hover:bg-brand-reddark transition-colors" data-testid="account-book-with-pack">
            {bundles.remaining > 0 ? "Book with pack" : "Buy a pack"}
          </Link>
        </div>

        <div className="mt-10">
          <h2 className="font-display text-2xl flex items-center gap-2"><CalendarDays className="w-6 h-6 text-brand-red" /> My sessions</h2>
          <div className="mt-4 space-y-3">
            {bookings.length === 0 ? (
              <div className="bg-white border border-brand-line rounded-2xl p-8 text-center text-brand-ink/50" data-testid="account-bookings-empty">
                No sessions booked yet. <Link to="/book" className="text-brand-red hover:underline">Book one now</Link>.
              </div>
            ) : bookings.map((b) => (
              <div key={b.id} className="bg-white border border-brand-line rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3" data-testid={`account-booking-${b.id}`}>
                <div>
                  <div className="font-medium flex items-center gap-2"><PawPrint className="w-4 h-4 text-brand-red" /> {b.pet_name || "Your pup"}</div>
                  <div className="text-sm text-brand-ink/50 mt-1">{b.date} {b.time} · {b.service_name || "Myofascial Yoga Session"}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-3 py-1 rounded-full capitalize ${STATUS_STYLES[b.status] || "bg-brand-mist text-brand-ink/60"}`} data-testid={`account-booking-status-${b.id}`}>{b.status}</span>
                  {canManage(b.status) && (
                    <>
                      <button onClick={() => openReschedule(b)} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-brand-line hover:border-brand-red hover:text-brand-red transition-colors" data-testid={`reschedule-btn-${b.id}`}><CalendarClock className="w-3.5 h-3.5" /> Reschedule</button>
                      <button onClick={() => cancelBooking(b.id)} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-brand-line hover:border-red-500 hover:text-red-600 transition-colors" data-testid={`cancel-btn-${b.id}`}><X className="w-3.5 h-3.5" /> Cancel</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10">
          <h2 className="font-display text-2xl flex items-center gap-2"><Users className="w-6 h-6 text-brand-red" /> My waitlists</h2>
          <div className="mt-4 space-y-3">
            {waitlist.length === 0 ? (
              <div className="bg-white border border-brand-line rounded-2xl p-6 text-center text-brand-ink/50 text-sm" data-testid="account-waitlist-empty">
                You're not on any waitlists. Full class? Join a waitlist from the <Link to="/schedule" className="text-brand-red hover:underline">schedule</Link> and we'll email you the moment a spot opens.
              </div>
            ) : waitlist.map((w) => (
              <div key={w.id} className="bg-white border border-brand-line rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3" data-testid={`account-waitlist-${w.id}`}>
                <div>
                  <div className="font-medium">{w.park_name || "Class"}</div>
                  <div className="text-sm text-brand-ink/50 mt-1 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {w.date} {w.time}</div>
                </div>
                <span className="text-xs px-3 py-1 rounded-full bg-brand-red/10 text-brand-red font-medium" data-testid={`waitlist-position-${w.id}`}>Position #{w.position}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {reschedule && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="reschedule-modal">
          <div className="bg-white rounded-2xl w-full max-w-sm p-8">
            <div className="flex items-center justify-between mb-6"><h2 className="font-display text-2xl">Reschedule session</h2><button onClick={() => setReschedule(null)}><X className="w-5 h-5" /></button></div>
            <form onSubmit={saveReschedule} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-brand-ink/50 font-semibold">New date</label>
                <input type="date" required value={rform.date} onChange={(e) => setRform({ ...rform, date: e.target.value })} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid="reschedule-date" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-brand-ink/50 font-semibold">New time</label>
                <input type="time" required value={rform.time} onChange={(e) => setRform({ ...rform, time: e.target.value })} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 focus:ring-2 focus:ring-brand-red focus:outline-none" data-testid="reschedule-time" />
              </div>
              <button type="submit" className="w-full px-6 py-3 rounded-full bg-brand-red text-white font-medium hover:bg-brand-reddark transition-colors" data-testid="reschedule-save-btn">Save new time</button>
            </form>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
